import { useTranslation } from "react-i18next";
import {
  DocumentCategory,
  type FieldStatusDto,
  type SourcingEventV2Dto,
  useListTextDocumentsForSourcingEventV2Query,
} from "../../../../../../autogen/bff-api";
import { DOCUMENT_CATEGORY_ORDER } from "../../../../../../common/documents/useDocumentCategoryLabel";
import type { PublicEventStep } from "../../wizardSteps";
import { DocumentCategoryGroup } from "./DocumentCategoryGroup";
import { SummarySection } from "./SummarySection";

interface DocumentsSectionProps {
  event: SourcingEventV2Dto;
  fieldStatuses: FieldStatusDto[];
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const DocumentsSection = ({ event, fieldStatuses, onWizardStepNavigate }: DocumentsSectionProps) => {
  const { t } = useTranslation();
  const documents = event.eventData.documents ?? [];
  const { data: textDocumentsData, isLoading: isLoadingTextDocuments } = useListTextDocumentsForSourcingEventV2Query({
    eventId: event.id,
  });

  const uncategorizedTextDocuments = (textDocumentsData?.documents ?? []).map((d) => ({
    id: d.id,
    name: d.name || t("Untitled document"),
  }));

  const documentsForCategory = (category: DocumentCategory) =>
    category === DocumentCategory.Other
      ? [
          ...documents.filter((d) => d.category == null || d.category === DocumentCategory.Other),
          ...uncategorizedTextDocuments,
        ]
      : documents.filter((d) => d.category === category);

  return (
    <SummarySection
      title={t("Documents")}
      editTarget={{ kind: "wizard-step", step: "documents" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      {!isLoadingTextDocuments &&
        DOCUMENT_CATEGORY_ORDER.map((category) => (
          <DocumentCategoryGroup
            key={category}
            category={category}
            documents={documentsForCategory(category)}
          />
        ))}
    </SummarySection>
  );
};
